// src/Form.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Form.css";
import ComparisonTable from "./components/ComparisonTable";
import { fetchMultiRoomPrediction } from "./api/predictionApi";
import { buildProject } from "./utils/projectBuilder";
import { auth } from "./services/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./services/firebase";

const CANVAS_SCALE = 0.32;

// ---------------------------------------------------------------------------
// Small reusable sub-components
// ---------------------------------------------------------------------------

const FormCard = ({ title, children, style }) => (
  <div className="fc-card" style={style}>
    {title && (
      <div className="fc-card-header">
        <span className="fc-card-title">{title}</span>
      </div>
    )}
    {children}
  </div>
);

const StepBar = ({ current = 1 }) => {
  const steps = [
    { n: 1, label: "Upload photos", sub: "Width + Length with AI Stick" },
    { n: 2, label: "Optional inputs", sub: "Dimensions & cost to compare" },
    { n: 3, label: "Analyze & save", sub: "Review results, save each room" },
  ];
  return (
    <div className="fc-stepbar">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`fc-step ${current >= s.n ? "fc-step--active" : ""}`}>
            <div className="fc-step-num">{current > s.n ? "✓" : s.n}</div>
            <div className="fc-step-text">
              <strong>{s.label}</strong>
              <span>{s.sub}</span>
            </div>
          </div>
          {i < steps.length - 1 && <div className="fc-step-connector" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const UploadZone = ({ label, fileName, preview, canvasRef, onFileChange, disabled }) => {
  const inputRef = useRef(null);
  const filled = !!fileName;

  return (
    <div className="fc-upload-group">
      <div
        className={`fc-upload-zone ${filled ? "fc-upload-zone--filled" : ""} ${disabled ? "fc-upload-zone--disabled" : ""}`}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onFileChange}
          disabled={disabled}
        />
        <span className="fc-upload-icon">{filled ? "✓" : "↑"}</span>
        <p className="fc-upload-label">{filled ? `${fileName}` : label}</p>
        <p className="fc-upload-sub">{filled ? "Click to replace" : "Click to choose file"}</p>
      </div>

      {preview && (
        <div className="fc-preview">
          <canvas
            ref={canvasRef}
            className="fc-preview-canvas"
          />
          <span className="fc-preview-badge">AI Stick</span>
        </div>
      )}
    </div>
  );
};

const MetricStrip = ({ items }) => (
  <div className="fc-metrics">
    {items.map(({ label, value, highlight, color }) => (
      <div key={label} className="fc-metric">
        <div className="fc-metric-label">{label}</div>
        <div
          className="fc-metric-value"
          style={highlight ? { color: "var(--fc-blue)" } : color ? { color } : undefined}
        >
          {value}
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Main Form component
// ---------------------------------------------------------------------------

const Form = () => {
  const navigate = useNavigate();

  const [currentRoomIndex, setCurrentRoomIndex] = useState(1);
  const [projectName, setProjectName] = useState("");
  const [projectNameError, setProjectNameError] = useState(null);
  const projectNameRef = useRef(null);

  const [formData, setFormData] = useState({
    width: "",
    length: "",
    costPerSqFt: "",
    widthImage: null,
    lengthImage: null,
  });

  const [widthPreview, setWidthPreview] = useState(null);
  const [lengthPreview, setLengthPreview] = useState(null);
  const [widthFileName, setWidthFileName] = useState(null);
  const [lengthFileName, setLengthFileName] = useState(null);

  const [topPredictions, setTopPredictions] = useState({ width: null, length: null });
  const [estimates, setEstimates] = useState(null);
  const [loading, setLoading] = useState(false);

  const [rooms, setRooms] = useState([]);
  const roomsRef = useRef([]);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);

  const [analysisId, setAnalysisId] = useState(null);
  const [lastSavedAnalysisId, setLastSavedAnalysisId] = useState(null);
  const [saveHint, setSaveHint] = useState(null);
  const [isRoomFinalized, setIsRoomFinalized] = useState(false);
  const [uploadResetKey, setUploadResetKey] = useState(0);

  const widthCanvasRef = useRef(null);
  const lengthCanvasRef = useRef(null);

  const isProjectNameLocked = rooms.length > 0;

  // Determine stepper progress
  const stepperCurrent = useMemo(() => {
    if (estimates) return 3;
    if (formData.widthImage || formData.lengthImage) return 2;
    return 1;
  }, [estimates, formData.widthImage, formData.lengthImage]);

  // ---------------------------------------------------------------------------
  // Project name helpers
  // ---------------------------------------------------------------------------
  const getCleanProjectName = () => String(projectName || "").trim();

  const requireProjectName = () => {
    const clean = getCleanProjectName();
    if (!clean) {
      setProjectNameError("Project name is required.");
      try {
        projectNameRef.current?.focus();
        projectNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
      return false;
    }
    setProjectNameError(null);
    return true;
  };

  useEffect(() => {
    if (projectNameError && getCleanProjectName()) setProjectNameError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName]);

  // ---------------------------------------------------------------------------
  // Canvas drawing
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const drawCanvas = (previewUrl, canvasRef, prediction, measurementFt, measureType) => {
      if (!previewUrl || !canvasRef.current) return;
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const scale = CANVAS_SCALE;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // ── AI Stick bounding box ──
        if (prediction?.boundingBox) {
          const { left, top, width, height } = prediction.boundingBox;
          const x = left * canvas.width;
          const y = top * canvas.height;
          const w = width * canvas.width;
          const h = height * canvas.height;
          // Scale line width relative to canvas size so it's visible on mobile
          const scale = canvas.width / 800;
          ctx.strokeStyle = "#ef4444";
          ctx.lineWidth = Math.max(3, 4 * scale);
          ctx.strokeRect(x, y, w, h);
          ctx.font = `bold ${Math.max(16, Math.round(20 * scale))}px sans-serif`;
          ctx.fillStyle = "#ef4444";
          ctx.fillText("AI Stick", x, Math.max(20, y - 8));
        }

        // ── Measurement line (shown after Analyze completes) ──
        if (measurementFt != null) {
          // Scale all drawing values relative to canvas size
          const scale = canvas.width / 800;
          const PAD = Math.round(24 * scale);
          const MID_RATIO = 0.72;
          const COLOR = measureType === "width" ? "#00e0ff" : "#ff00c8";
          const label = `${measureType === "width" ? "Width" : "Length"}: ${measurementFt.toFixed(2)} ft`;

          ctx.save();
          ctx.strokeStyle = COLOR;
          ctx.fillStyle = COLOR;
          ctx.lineWidth = Math.max(4, 6 * scale);
          ctx.setLineDash([]);

          let x1, y1, x2, y2;

          if (measureType === "width") {
            y1 = y2 = canvas.height * MID_RATIO;
            x1 = PAD;
            x2 = canvas.width - PAD;
          } else {
            x1 = x2 = canvas.width * MID_RATIO;
            y1 = PAD;
            y2 = canvas.height - PAD;
          }

          // Main line
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();

          // Endpoint dots
          const dotRadius = Math.max(8, 12 * scale);
          [{ x: x1, y: y1 }, { x: x2, y: y2 }].forEach(({ x, y }) => {
            ctx.beginPath();
            ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
            ctx.fill();
          });

          // End tick marks
          const TICK = Math.max(14, 20 * scale);
          ctx.lineWidth = Math.max(3, 5 * scale);
          if (measureType === "width") {
            ctx.beginPath(); ctx.moveTo(x1, y1 - TICK); ctx.lineTo(x1, y1 + TICK); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(x2, y2 - TICK); ctx.lineTo(x2, y2 + TICK); ctx.stroke();
          } else {
            ctx.beginPath(); ctx.moveTo(x1 - TICK, y1); ctx.lineTo(x1 + TICK, y1); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(x2 - TICK, y2); ctx.lineTo(x2 + TICK, y2); ctx.stroke();
          }

          // Label pill centered on the line
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const fontSize = Math.max(18, Math.round(22 * scale));
          ctx.font = `bold ${fontSize}px sans-serif`;
          const textW = ctx.measureText(label).width;
          const pillPad = Math.max(16, 20 * scale);
          const pillW = textW + pillPad * 2;
          const pillH = fontSize + pillPad;
          const pillX = midX - pillW / 2;
          const pillY = midY - pillH / 2;

          // Pill background
          ctx.fillStyle = "rgba(0,0,0,0.65)";
          ctx.beginPath();
          ctx.roundRect(pillX, pillY, pillW, pillH, Math.max(6, 8 * scale));
          ctx.fill();

          // Pill text
          ctx.fillStyle = COLOR;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(label, midX, midY);

          ctx.restore();
        }
      };
      img.src = previewUrl;
    };

    drawCanvas(widthPreview, widthCanvasRef, topPredictions.width, estimates?.width, "width");
    drawCanvas(lengthPreview, lengthCanvasRef, topPredictions.length, estimates?.length, "length");
  }, [widthPreview, lengthPreview, topPredictions, estimates]);

  // ---------------------------------------------------------------------------
  // Input handling
  // ---------------------------------------------------------------------------
  const handleChange = (e) => {
    if (isRoomFinalized) return;
    const { name, value, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        if (name === "widthImage") {
          setWidthPreview(reader.result);
          setWidthFileName(file.name);
        }
        if (name === "lengthImage") {
          setLengthPreview(reader.result);
          setLengthFileName(file.name);
        }
      };
      reader.readAsDataURL(file);
      setFormData((prev) => ({ ...prev, [name]: file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Upload zone specific handler (wraps handleChange with correct name)
  const makeUploadHandler = (name) => (e) => {
    if (!e.target.files?.[0]) return;
    handleChange({ target: { name, files: e.target.files } });
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const getTopPrediction = (predictions = []) => {
    const sticks = predictions.filter((p) => p.tagName?.toLowerCase().includes("stick"));
    return sticks.sort(
      (a, b) => (b.probability ?? b.confidence ?? 0) - (a.probability ?? a.confidence ?? 0)
    )[0] || null;
  };

  const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return `$${n.toFixed(2)}`;
  };

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const confidencePct = useMemo(() => {
    const w = topPredictions.width?.probability ?? topPredictions.width?.confidence;
    const l = topPredictions.length?.probability ?? topPredictions.length?.confidence;
    const vals = [w, l].filter((x) => typeof x === "number" && isFinite(x));
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100);
  }, [topPredictions]);

  const currentComparisonRows = useMemo(() => {
    if (!estimates?.width && !estimates?.length) return [];
    const userW = toNum(formData.width);
    const userL = toNum(formData.length);
    const aiW = estimates?.width;
    const aiL = estimates?.length;
    const rows = [];
    if (aiW != null) {
      rows.push({
        label: "Width",
        user: userW != null ? userW.toFixed(2) : "—",
        ai: aiW,
        delta: userW != null ? aiW - userW : aiW,
      });
    }
    if (aiL != null) {
      rows.push({
        label: "Length",
        user: userL != null ? userL.toFixed(2) : "—",
        ai: aiL,
        delta: userL != null ? aiL - userL : aiL,
      });
    }
    return rows;
  }, [estimates, formData.width, formData.length]);

  // ---------------------------------------------------------------------------
  // Analyze
  // ---------------------------------------------------------------------------
  const handleAnalyze = async () => {
    if (!requireProjectName()) return;
    if (isRoomFinalized) return;
    if (!formData.widthImage || !formData.lengthImage) {
      alert("Please upload both width and length images.");
      return;
    }
    setLoading(true);
    try {
      const result = await fetchMultiRoomPrediction(formData.widthImage, formData.lengthImage);
      const widthPrediction = getTopPrediction(result.widthResult?.sticks);
      const lengthPrediction = getTopPrediction(result.lengthResult?.sticks);
      if (!widthPrediction && !lengthPrediction) alert("No AI Stick detected in either image.");
      setTopPredictions({ width: widthPrediction, length: lengthPrediction });

      const calcDimension = (prediction, imageSrc) => {
        if (!prediction?.boundingBox) return null;
        const img = new Image();
        img.src = imageSrc;
        return new Promise((resolve) => {
          img.onload = () => {
            const pixelSize = prediction.boundingBox.width * img.width;
            if (!pixelSize) return resolve(null);
            resolve(img.width / pixelSize);
          };
        });
      };

      const widthInFeet = widthPrediction ? await calcDimension(widthPrediction, widthPreview) : null;
      const lengthInFeet = lengthPrediction ? await calcDimension(lengthPrediction, lengthPreview) : null;
      const totalArea = widthInFeet && lengthInFeet ? widthInFeet * lengthInFeet : null;
      const costPerSqFtNum = toNum(formData.costPerSqFt);
      const cost = totalArea && costPerSqFtNum != null ? totalArea * costPerSqFtNum : null;

      setEstimates({ width: widthInFeet, length: lengthInFeet, area: totalArea, cost });
      const newId = String(Date.now());
      setAnalysisId(newId);
      setLastSavedAnalysisId(null);
      setIsRoomFinalized(false);
      setSaveHint(null);
    } catch (err) {
      console.error("Error during analysis:", err);
      alert("AI analysis failed. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------
  const clearCurrentRoom = (keepCost = true) => {
    setFormData((prev) => ({
      width: "",
      length: "",
      costPerSqFt: keepCost ? prev.costPerSqFt : "",
      widthImage: null,
      lengthImage: null,
    }));
    setWidthPreview(null);
    setLengthPreview(null);
    setWidthFileName(null);
    setLengthFileName(null);
    setTopPredictions({ width: null, length: null });
    setEstimates(null);
    setAnalysisId(null);
    setLastSavedAnalysisId(null);
    setSaveHint(null);
    setIsRoomFinalized(false);
    setUploadResetKey((k) => k + 1); // force UploadZone remount → clears file inputs
  };

  const handleReset = () => clearCurrentRoom(true);

  // ---------------------------------------------------------------------------
  // Save room
  // ---------------------------------------------------------------------------
  const saveRoomSnapshot = (startNextRoom) => {
    if (!requireProjectName()) return;
    if (!estimates?.width && !estimates?.length) {
      alert("Please analyze this room before saving it.");
      return;
    }
    if (analysisId && lastSavedAnalysisId === analysisId) return;

    const userW = toNum(formData.width);
    const userL = toNum(formData.length);
    const costPerSqFtNum = toNum(formData.costPerSqFt);
    const aiW = estimates?.width ?? null;
    const aiL = estimates?.length ?? null;
    const aiArea = aiW != null && aiL != null ? Number(aiW) * Number(aiL) : null;
    const aiCost = aiArea != null && costPerSqFtNum != null ? aiArea * costPerSqFtNum : null;
    const userArea = userW != null && userL != null ? userW * userL : null;
    const userCost = userArea != null && costPerSqFtNum != null ? userArea * costPerSqFtNum : null;

    setRooms((prev) => {
      const roomNumber = prev.length + 1;
      const label = `Room ${roomNumber}`;
      const roomSnapshot = {
        id: (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()),
        label,
        widthPreview,
        lengthPreview,
        userWidth: userW,
        userLength: userL,
        costPerSqFt: costPerSqFtNum,
        aiWidth: aiW,
        aiLength: aiL,
        aiArea,
        aiCost,
        userArea,
        userCost,
        confidencePct: confidencePct ?? null,
        comparisonRows: [
          {
            label: "Width",
            user: userW != null ? userW.toFixed(2) : "—",
            ai: aiW,
            delta: userW != null && aiW != null ? aiW - userW : aiW ?? 0,
          },
          {
            label: "Length",
            user: userL != null ? userL.toFixed(2) : "—",
            ai: aiL,
            delta: userL != null && aiL != null ? aiL - userL : aiL ?? 0,
          },
        ].filter((r) => r.ai != null),
      };
      return [...prev, roomSnapshot];
    });

    setLastSavedAnalysisId(analysisId || "saved");

    if (startNextRoom) {
      setCurrentRoomIndex((n) => n + 1);
      setSaveHint(null);
      setIsRoomFinalized(false);
      clearCurrentRoom(true);
    } else {
      setIsRoomFinalized(true);
      setSaveHint("Room saved. Click Done to exit, or Save & Next Room to add another room.");
    }
  };

  const handleSaveRoom = () => saveRoomSnapshot(false);
  const handleSaveAndNextRoom = () => saveRoomSnapshot(true);

  // ---------------------------------------------------------------------------
  // Project summary
  // ---------------------------------------------------------------------------
  const projectSummary = useMemo(() => {
    const roomsCount = rooms.length;
    const totalAiArea = rooms.reduce((s, r) => s + (r.aiArea ?? 0), 0);
    const totalAiCost = rooms.reduce((s, r) => s + (r.aiCost ?? 0), 0);
    const totalUserArea = rooms.reduce((s, r) => s + (r.userArea ?? 0), 0);
    const totalUserCost = rooms.reduce((s, r) => s + (r.userCost ?? 0), 0);
    const confidences = rooms.map((r) => r.confidencePct).filter((x) => typeof x === "number" && isFinite(x));
    const avgConfidence = confidences.length
      ? Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length)
      : null;
    return { roomsCount, totalAiArea, totalAiCost, totalUserArea, totalUserCost, avgConfidence };
  }, [rooms]);

  // ---------------------------------------------------------------------------
  // Firestore persist
  // ---------------------------------------------------------------------------
  const persistProjectToFirestore = async () => {
    if (!auth.currentUser) throw new Error("User not authenticated");

    // Always derive summary from roomsRef.current (never from stale closure state)
    const liveRooms = roomsRef.current;
    const liveSummary = {
      roomsCount:    liveRooms.length,
      totalAiArea:   liveRooms.reduce((s, r) => s + (r.aiArea   ?? 0), 0),
      totalAiCost:   liveRooms.reduce((s, r) => s + (r.aiCost   ?? 0), 0),
      totalUserArea: liveRooms.reduce((s, r) => s + (r.userArea  ?? 0), 0),
      totalUserCost: liveRooms.reduce((s, r) => s + (r.userCost  ?? 0), 0),
      avgConfidence: (() => {
        const vals = liveRooms
          .map((r) => r.confidencePct)
          .filter((x) => typeof x === "number" && isFinite(x));
        return vals.length
          ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
          : null;
      })(),
    };

    const project = buildProject({
      ownerUser: auth.currentUser,
      mode: "multiroom",
      rooms: liveRooms,
      summary: liveSummary,
    });
    const projectId = crypto.randomUUID();
    await setDoc(doc(db, "projects", projectId), {
      ...project,
      id: projectId,
      projectName: getCleanProjectName(),
      ownerUid: auth.currentUser.uid,
      ownerEmail: auth.currentUser.email,
      projectType: "multiroom",
      status: "private",
      sharedWithContractor: false,
      sharedAt: null,
      createdAt: serverTimestamp(),
    });
    return projectId;
  };

  const handleDone = async () => {
    if (roomsRef.current.length > 0) {
      if (!requireProjectName()) return;
      try {
        const projectId = await persistProjectToFirestore();
        console.log("✅ NEW projectId (after save):", projectId);
        await fetch("/api/share-project", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId }),
        });
      } catch (e) {
        console.error("Failed to save project:", e);
        alert("Failed to save project. Please try again.");
        return;
      }
    }
    navigate("/dashboard");
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const canSave =
    !loading &&
    !isRoomFinalized &&
    !!estimates &&
    !(analysisId && lastSavedAnalysisId === analysisId);

  return (
    <div className="fc-root">
      {/* ── Step progress bar ── */}
      <StepBar current={stepperCurrent} />

      <div className="fc-body">

        {/* ── Project name ── */}
        <FormCard title={`Analyze Room ${currentRoomIndex}`}>
          <div className="fc-field">
            <label className="fc-label">
              Project name <span className="fc-req">*</span>
            </label>
            <input
              ref={projectNameRef}
              className={`fc-input ${projectNameError ? "fc-input--error" : ""} ${isProjectNameLocked ? "fc-input--locked" : ""}`}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Kitchen Remodel"
              disabled={loading || isProjectNameLocked}
            />
            {projectNameError && (
              <p className="fc-field-error">{projectNameError}</p>
            )}
            {isProjectNameLocked && (
              <span className="fc-locked-tag">Locked after first room save</span>
            )}
          </div>
        </FormCard>

        {/* ── Photo uploads ── */}
        <FormCard title="Upload photos">
          <div className="fc-grid-2">
            <UploadZone
              key={`width-${uploadResetKey}`}
              label="Width photo"
              fileName={widthFileName}
              preview={widthPreview}
              canvasRef={widthCanvasRef}
              onFileChange={makeUploadHandler("widthImage")}
              disabled={isRoomFinalized || loading}
            />
            <UploadZone
              key={`length-${uploadResetKey}`}
              label="Length photo"
              fileName={lengthFileName}
              preview={lengthPreview}
              canvasRef={lengthCanvasRef}
              onFileChange={makeUploadHandler("lengthImage")}
              disabled={isRoomFinalized || loading}
            />
          </div>
        </FormCard>

        {/* ── Optional measurements ── */}
        <FormCard title="Optional measurements">
          <div className="fc-grid-3">
            <div className="fc-field">
              <label className="fc-label">Room width (ft)</label>
              <input
                className="fc-input"
                name="width"
                value={formData.width}
                onChange={handleChange}
                inputMode="decimal"
                placeholder="Optional"
                disabled={isRoomFinalized || loading}
              />
            </div>
            <div className="fc-field">
              <label className="fc-label">Room length (ft)</label>
              <input
                className="fc-input"
                name="length"
                value={formData.length}
                onChange={handleChange}
                inputMode="decimal"
                placeholder="Optional"
                disabled={isRoomFinalized || loading}
              />
            </div>
            <div className="fc-field">
              <label className="fc-label">Labor cost / sq ft ($)</label>
              <input
                className="fc-input"
                name="costPerSqFt"
                value={formData.costPerSqFt}
                onChange={handleChange}
                inputMode="decimal"
                placeholder="Optional"
                disabled={isRoomFinalized || loading}
              />
            </div>
          </div>
        </FormCard>

        {/* ── Action buttons ── */}
        <div className="fc-actions">
          <button
            className="fc-btn fc-btn--primary"
            onClick={handleAnalyze}
            disabled={loading || isRoomFinalized}
          >
            {loading ? (
              <><span className="fc-spinner" /> Analyzing…</>
            ) : (
              "▶ Analyze"
            )}
          </button>

          <button
            className="fc-btn fc-btn--ghost"
            onClick={handleReset}
            disabled={loading || isRoomFinalized}
          >
            ↺ Reset
          </button>

          <button
            className="fc-btn fc-btn--save"
            onClick={handleSaveRoom}
            disabled={!canSave}
          >
            Save Room
          </button>

          <button
            className="fc-btn fc-btn--next"
            onClick={handleSaveAndNextRoom}
            disabled={!canSave}
          >
            Save &amp; Next Room →
          </button>

          <button
            className="fc-btn fc-btn--done"
            onClick={handleDone}
            disabled={loading}
          >
            Done ✓
          </button>
        </div>

        {/* ── Save hint ── */}
        {saveHint && (
          <div className="fc-hint">
            {saveHint}
          </div>
        )}

        {/* ── Results ── */}
        {(estimates?.width != null || estimates?.length != null) && (
          <FormCard title="AI analysis results">
            <MetricStrip
              items={[
                {
                  label: "AI Width",
                  value: estimates?.width != null ? `${estimates.width.toFixed(2)} ft` : "—",
                  highlight: true,
                },
                {
                  label: "AI Length",
                  value: estimates?.length != null ? `${estimates.length.toFixed(2)} ft` : "—",
                  highlight: true,
                },
                {
                  label: "AI Area",
                  value: estimates?.area != null ? `${estimates.area.toFixed(2)} sq ft` : "—",
                },
                {
                  label: "Est. Cost",
                  value: estimates?.cost != null ? formatMoney(estimates.cost) : "—",
                },
                {
                  label: "Confidence",
                  value: confidencePct != null ? `${confidencePct}%` : "—",
                  color: confidencePct >= 90 ? "#16a34a" : confidencePct >= 70 ? "#ca8a04" : "#dc2626",
                },
              ]}
            />
            {currentComparisonRows.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <ComparisonTable rows={currentComparisonRows} />
              </div>
            )}
          </FormCard>
        )}

        {/* ── Saved rooms list ── */}
        {rooms.length > 0 && (
          <FormCard title="Rooms saved">
            <div className="fc-room-chips">
              {rooms.map((r) => (
                <span key={r.id} className="fc-room-chip">
                  <span className="fc-room-chip-dot" />
                  {r.label} &bull; {r.aiArea != null ? `${r.aiArea.toFixed(1)} sq ft` : "—"}
                  {r.confidencePct != null ? ` · ${r.confidencePct}%` : ""}
                </span>
              ))}
            </div>

            {rooms.map((r) => (
              <div key={r.id} className="fc-saved-room">
                <div className="fc-saved-room-label">{r.label}</div>
                <div className="fc-grid-2" style={{ marginBottom: 12 }}>
                  {r.widthPreview && (
                    <img src={r.widthPreview} alt={`${r.label} width`} className="fc-room-img" />
                  )}
                  {r.lengthPreview && (
                    <img src={r.lengthPreview} alt={`${r.label} length`} className="fc-room-img" />
                  )}
                </div>
                <ComparisonTable rows={r.comparisonRows} />
                <div className="fc-room-stats">
                  <span>AI Area: {r.aiArea != null ? `${r.aiArea.toFixed(2)} sq ft` : "—"}</span>
                  <span className="fc-dot">·</span>
                  <span>AI Cost: {r.aiCost != null ? formatMoney(r.aiCost) : "—"}</span>
                  <span className="fc-dot">·</span>
                  <span>Confidence: {r.confidencePct != null ? `${r.confidencePct}%` : "—"}</span>
                </div>
              </div>
            ))}
          </FormCard>
        )}

        {/* ── Project summary ── */}
        {rooms.length > 0 && (
          <FormCard title="Project summary">
            <MetricStrip
              items={[
                { label: "Total rooms", value: projectSummary.roomsCount },
                { label: "Total AI area", value: `${projectSummary.totalAiArea.toFixed(2)} sq ft` },
                { label: "Total AI cost", value: formatMoney(projectSummary.totalAiCost) },
                { label: "Total user area", value: `${projectSummary.totalUserArea.toFixed(2)} sq ft` },
                {
                  label: "Avg confidence",
                  value: projectSummary.avgConfidence != null ? `${projectSummary.avgConfidence}%` : "—",
                  color: "#16a34a",
                },
              ]}
            />
            <div className="fc-summary-user-cost">
              Total user cost: <strong>{formatMoney(projectSummary.totalUserCost)}</strong>
            </div>
          </FormCard>
        )}

      </div>
    </div>
  );
};

export default Form;
